import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import learnPlusLogoUrl from '../../assets/learnplus-wordmark.svg';
import { useAuth } from '../../lib/auth';
import { learnerNavFromSummary } from '../lib/parent-portal-api';
import { fetchMyLearners } from '../lib/registration-api';
import { ParentShell } from './ParentShell';

export interface AppShellProps {
  children: ReactNode;
  /** Rendered on the right of the header — a sign-in link, or the parent's name. */
  headerActions?: ReactNode;
  /** Rendered in the portal top bar for authenticated pages. */
  title?: string;
  /** Layout of the main column. */
  width?: 'form' | 'wide' | 'narrow';
  /** Vertically centres short content, e.g. the sign-in card. */
  centred?: boolean;
}

/**
 * The frame every parent-facing page sits in: header, logo, brand stripe and
 * the layout column.
 *
 * Pages pick a column with `width` rather than setting their own max-width, so
 * the form pages and the dashboard stay consistent with each other.
 *
 * Public chrome uses the white Learn Plus wordmark on the navy header.
 * Cambridge sits under the login and registration actions (`AuthEndorsement`).
 */
function AuthenticatedFrame({
  children,
  headerActions,
  title,
  width,
}: Pick<AppShellProps, 'children' | 'headerActions' | 'title' | 'width'>): ReactElement {
  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
  });
  const learners = learnerNavFromSummary(learnersQuery.data ?? []);
  const pageClass = width === 'wide' ? 'portal-page-wide' : 'portal-page';
  return (
    <ParentShell learners={learners} title={title} headerActions={headerActions}>
      <div className={pageClass}>{children}</div>
    </ParentShell>
  );
}

export function AppShell({
  children,
  headerActions,
  title,
  width = 'form',
  centred = false,
}: AppShellProps): ReactElement {
  const { token } = useAuth();
  const shellClass = [
    'shell',
    width === 'wide' ? 'shell-wide' : '',
    width === 'narrow' ? 'shell-narrow' : '',
    centred ? 'shell-centred' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (token) {
    return (
      <AuthenticatedFrame headerActions={headerActions} title={title} width={width}>
        {children}
      </AuthenticatedFrame>
    );
  }

  return (
    <>
      <header className="app-header">
        <div className="app-header-inner">
          <Link className="brand" to="/">
            <img className="brand-lockup brand-lockup-on-navy" src={learnPlusLogoUrl} alt="Learn Plus" />
          </Link>
          {headerActions ? <div className="header-actions">{headerActions}</div> : null}
        </div>
      </header>
      <div className="brand-stripe" aria-hidden="true" />
      <main className={shellClass}>{children}</main>
    </>
  );
}
